
import React from 'react';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';

interface ServiceCardProps {
  title: string;
  description: string;
  tag: string;
  emoji: React.ReactNode;
  phase: string;
  columnColor: string;
  actionText?: string;
  isSelected?: boolean;
  isComparing?: boolean;
  onSelect?: () => void;
}

const getEmojiFromTag = (tag: string): string => {
  // Extract just the emoji from the tag
  const match = tag.match(/^([\u{1F300}-\u{1F6FF}\u{1F900}-\u{1F9FF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}])/u);
  return match ? match[1] : '🔹'; // Default emoji if none found
};

const ServiceCard = ({ 
  title, 
  description, 
  actionText, 
  tag, 
  emoji,
  phase,
  columnColor
}: ServiceCardProps) => {
  // Get just the emoji from the tag
  const tagEmoji = getEmojiFromTag(tag);
  
  return (
    <motion.div
      className={cn(
        `p-6 rounded-xl border relative overflow-hidden transition-all duration-300 group`,
        "border-white/20 hover:border-white/40",
        "bg-white/10 hover:bg-white/15"
      )}
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      viewport={{ once: true }}
      whileHover={{ 
        scale: 1.01,
        boxShadow: "0 8px 16px rgba(0, 0, 0, 0.15)"
      }}
    >
      <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-all duration-500"></div>

      <div className="flex items-center mb-4">
        <div className="mr-3 bg-white/20 p-2.5 rounded-md shadow-sm border border-white/20 flex items-center justify-center">
          {emoji}
        </div>
        <h4 className="text-lg md:text-xl font-semibold text-white">{title}</h4>
      </div>
      
      <p className="text-base text-gray-200 mb-4 min-h-[60px] leading-relaxed">{description}</p>
      
      <div className="mt-auto flex items-center">
        <span className={cn(
          "text-xl w-10 h-10 flex items-center justify-center rounded-full", 
          "bg-white/15 text-white shadow-sm border border-white/10"
        )}>
          {tagEmoji}
        </span>
      </div>
    </motion.div>
  );
};

export default ServiceCard;
